import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Logo } from "./Logo";
import MarylandCoverage from "./MarylandCoverage";
import { Mail, Phone, Shield, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";

// Bumped manually when shipping. Combined with backend /api/version,
// this gives us an immediate visual signal of which build is live.
const FRONTEND_VERSION = "2026.07.21-resources-hub";

export default function Footer() {
  const [backendVersion, setBackendVersion] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/version")
      .then((r) => {
        if (!cancelled) setBackendVersion(r.data);
      })
      .catch(() => {
        if (!cancelled) setBackendVersion({ version: "unreachable", started_at: null });
      });
    return () => { cancelled = true; };
  }, []);

  return (
 <footer data-testid="site-footer" className="relative mt-20 bg-ink text-cream/85">
 <div className="container-pad max-w-7xl mx-auto py-16">
 <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
 <div className="md:col-span-5">
 <Logo variant="dark" size="lg" />
 <p data-testid="footer-tagline" className="mt-5 font-serif-italic text-lg text-gold-400 leading-snug max-w-md">
 Elevating Wellness Through Exceptional Care
 </p>
 <p className="mt-5 text-sm leading-relaxed max-w-md text-cream/70">
 Compassionate, person-centred psychiatric care delivered virtually across the
 communities we serve.
 </p>
 <div className="mt-8">
 <MarylandCoverage tone="dark" />
 </div>
 <div className="mt-6 flex items-center gap-3 text-xs text-cream/55">
 <Shield className="w-4 h-4 text-gold-400" strokeWidth={1.5} />
 HIPAA-aligned, encrypted telehealth platform
 </div>
 </div>

 <div className="md:col-span-3">
 <p className="font-heading text-sm uppercase tracking-[0.22em] text-gold-400">Explore</p>
 <ul className="mt-5 space-y-3 text-sm">
 <li><Link to="/about" data-testid="footer-link-about" className="hover:text-gold-300 transition">About</Link></li>
 <li><Link to="/services" data-testid="footer-link-services" className="hover:text-gold-300 transition">Services</Link></li>
 <li><Link to="/how-it-works" data-testid="footer-link-how-it-works" className="hover:text-gold-300 transition">How it Works</Link></li>
 <li><Link to="/telehealth" data-testid="footer-link-telehealth" className="hover:text-gold-300 transition">Telehealth</Link></li>
 <li><Link to="/insurance" data-testid="footer-link-insurance" className="hover:text-gold-300 transition">Insurance</Link></li>
 <li><Link to="/faq" data-testid="footer-link-faq" className="hover:text-gold-300 transition">FAQ</Link></li>
 <li><Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-gold-300 transition">Security & Privacy</Link></li>
 <li><Link to="/contact" data-testid="footer-link-contact" className="hover:text-gold-300 transition">Contact / Book</Link></li>
 </ul>
 </div>

 <div className="md:col-span-4">
 <p className="font-heading text-sm uppercase tracking-[0.22em] text-gold-400">Reach us</p>
 <ul className="mt-5 space-y-4 text-sm">
 <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-0.5 text-gold-400" strokeWidth={1.5} /> care@goldenlifesh.com</li>
 <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-0.5 text-gold-400" strokeWidth={1.5} /> (443) 891-4248</li>
 </ul>
 <Link to="/contact" data-testid="footer-book-cta" className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-gold-400 hover:text-gold-300">
 Book a confidential consultation <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
 </Link>
 </div>
 </div>

 <div className="gold-rule my-12 opacity-40" />

 <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-cream/55">
 <p>© {new Date().getFullYear()} Golden Life Standard Health. All rights reserved.</p>
 <p className="max-w-xl">
 If you are experiencing a mental health emergency, call 988 (Suicide & Crisis Lifeline) or 911. This site does not provide emergency services.
 </p>
 </div>

 <div
 data-testid="footer-version-stamp"
 className="mt-6 text-[10px] tracking-wider text-cream/35 font-mono"
 title={backendVersion?.started_at ? `Backend started ${backendVersion.started_at}` : undefined}
 >
 build {FRONTEND_VERSION}
 {backendVersion ? ` · api ${backendVersion.version}` : ""}
 </div>
 </div>
 </footer>
 );
}
